import { Component } from 'react'
import { toast } from 'react-toastify'
import { IClientUID } from '../../../api/clients'
import { getClientCustomerData, updateClientCustomerData, ICustomerData, CustomerDataType } from '../../../api/0x0005/customerdata'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import classnames from 'classnames'
import { saveMachine } from '../../../api/0x0002/machine'

type Props = {
    clientId: IClientUID
}

type State = {
    settings?: Record<keyof ICustomerData, string>
    updates: Partial<ICustomerData>
    errors: Partial<Record<keyof ICustomerData, string>>
    unsaved: boolean
}

class CustomerDataTab extends Component<Props, State> {
    state: State = {
        settings: undefined,
        updates: {},
        errors: {},
        unsaved: false,
    }

    componentDidMount = () => {
        this.loadSettings()
    }

    loadSettings = async () => {
        try {
            const settings = (await getClientCustomerData(this.props.clientId)).data
            console.log('settings', settings)
            this.setState({ settings, updates: {}, errors: {}, unsaved: false })
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    async saveSettings() {
        try {
            const { updates, errors } = this.state
            if (Object.keys(updates).length === 0) {
                return toast.error(getString('settings.no_updates'))
            }
            if (Object.keys(errors).length > 0) {
                return toast.error(getString('settings.fix_errors'))
            }
            await updateClientCustomerData(
                this.props.clientId,
                Object.entries(updates).map(([key, value]) => ({
                    type: CustomerDataType[key as any] as any,
                    value: value!,
                })),
            )

            // Save the data in our database
            await saveMachine(this.props.clientId, this.state.settings!)

            // Clear updates after successful save
            this.setState({ updates: {}, unsaved: false })

            toast.success(getString('common.success', 'common.saved'))
        } catch (err) {
            toast.error((err as Error).message)
            console.error(err)
        }
    }

    handleChange(name: keyof ICustomerData, value: string) {
        const { settings, updates, errors } = this.state
        
        // Validate key field
        if (name === 'key') {
            if (value.length > 20) {
                errors[name] = getString('customerdata.key_max_length')
                this.setState({ errors })
                return
            }
            if (!/^[\x00-\x7F]*$/.test(value)) {
                errors[name] = getString('customerdata.key_english_only')
                this.setState({ errors })
                return
            }
            delete errors[name]
        }

        settings![name] = value
        updates[name] = value
        this.setState({ settings, updates, unsaved: true, errors })
    }

    render() {
        if (this.state.settings === undefined) return <Loader size={14} />
        return (
            <div>
                {this.renderActions()}
                {this.renderCustomerData()}
            </div>
        )
    }

    renderActions() {
        return (
            <div className="grid grid-cols-4 gap-5">
                {this.renderActionButton(getString('common.save'), this.saveSettings.bind(this))}
                {this.renderActionButton(getString('settings.refresh'), this.loadSettings.bind(this))}
                {this.renderUnsavedChanges()}
            </div>
        )
    }

    renderActionButton(label: string, onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) {
        return (
            <button
                onClick={onClick}
                className="group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 duration-200"
            >
                {label}
            </button>
        )
    }

    renderUnsavedChanges() {
        if (!this.state.unsaved) return undefined
        return <div className="dark:text-white flex items-center">* {getString('common.unsaved_changes')}</div>
    }

    renderInputErrorMessage(message: string) {
        return <div className="text-red-500 text-sm">{message}</div>
    }

    renderCustomerData() {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md mt-8 p-6">
                <div className="grid grid-cols-1 gap-6">
                    {this.renderInput('name', getString('customerdata.name'))}
                    {this.renderInput('address', getString('customerdata.address'))}
                    {this.renderInput('city', getString('customerdata.city'))}
                    {this.renderInput('telephone', getString('customerdata.telephone'))}
                    {this.renderInput('cellphone1', getString('customerdata.cellphone1'))}
                    {this.renderInput('cellphone2', getString('customerdata.cellphone2'))}
                    {this.renderInput('mail', getString('customerdata.mail'))}
                    {this.renderInput('company', getString('customerdata.company'))}
                    {this.renderTextarea('notes', getString('customerdata.notes'))}
                    {this.renderInput('key', getString('customerdata.key'), 20)}
                </div>
            </div>
        )
    }

    renderInput(name: keyof ICustomerData, label: string, maxLength?: number) {
        const { settings, errors } = this.state
        const error = errors[name] !== undefined
        return (
            <div>
                <label className="dark:text-white text-sm">{label}</label>
                {maxLength && (
                    <span className="text-xs text-gray-500 ml-2">
                        ({settings![name]?.length || 0}/{maxLength} {getString('customerdata.characters')})
                    </span>
                )}
                <input
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings![name] as string}
                    onInput={(e: any) => this.handleChange(name, e.target.value)}
                    maxLength={maxLength}
                />
                {error && this.renderInputErrorMessage(errors[name]!)}
            </div>
        )
    }

    renderTextarea(name: keyof ICustomerData, label: string) {
        const { settings, errors } = this.state
        const error = errors[name] !== undefined
        return (
            <div>
                <label className="dark:text-white text-sm">{label}</label>
                <textarea
                    className={classnames('pretty-input', error ? 'border-red-500' : 'border-gray-300')}
                    value={settings![name] as string}
                    onInput={(e: any) => this.handleChange(name, e.target.value)}
                    rows={4}
                />
                {error && this.renderInputErrorMessage(errors[name]!)}
            </div>
        )
    }
}

export default CustomerDataTab
